import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { agentAxiosObj } from "../../../config/apiClients";
import { queryParams } from "../../../utils/interface";
import { AgentTransactionStatsState } from "../../types";
import { buildUrlWithParams } from "../../../utils/helpers/common";
import { formatDuration } from "../../../utils/date";
import { LiveTransaction } from "../../../utils/types";

const initialState: AgentTransactionStatsState = {
  isLoading: false,
  transactionStats: [],
  liveTransactions: [],
  pagination: {
    totalItems: 0,
    currentPage: 1,
    totalPages: 1,
    hasNextPage: false,
  },
};


export const getAgentTransactionsStats = createAsyncThunk(
  "/transaction-stats",
  async ({ startDate, endDate }: queryParams, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();

      if (startDate) params.append("startDate", startDate as string);
      if (endDate) params.append("endDate", endDate as string);

      const response = await agentAxiosObj.get(
        `/transaction-stats?${params.toString()}`
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data || { message: "Failed to Fetch Agent Transaction Stats" }
      );
    }
  }
);

export const getAgentLiveTransactions = createAsyncThunk(
  "/agentliveTransactions",
  async (query: queryParams, { rejectWithValue }) => {
    try {
      const url = buildUrlWithParams("liveTransactions", query);
      const response = await agentAxiosObj.get(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Fetch Agent Assigned Clients Live Transactions' });
    }
  }
);

const agentTransactionStatsSlice = createSlice({
  name: "agentTransactionStats",
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(getAgentTransactionsStats.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAgentTransactionsStats.fulfilled, (state, action) => {
        state.isLoading = false;
        state.transactionStats =
          action.payload?.payload || [];
      })
      .addCase(getAgentTransactionsStats.rejected, (state) => {
        state.isLoading = false;
        state.transactionStats = [];
      })
      .addCase(getAgentLiveTransactions.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAgentLiveTransactions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.liveTransactions = action.payload?.payload.liveTransactions.map((item: LiveTransaction) => ({
          ...item,
          dealId: item.dealId ? item.dealId : '-',
          closePrice: item.closePrice ? item.closePrice : null,
          moneyBalance: item.moneyBalance ? item.moneyBalance : undefined,
          metalBalance: item.metalBalance ? item.metalBalance : undefined,
          timeDiff: item.typeDetail === "Trade" ? formatDuration(item.timeDiff) : "-",
          totalPnl: item.totalPnl ? item.totalPnl : null
        }));
        state.pagination = {
          totalItems: action.payload.payload.totalItems,
          currentPage: action.payload.payload.page,
          totalPages: action.payload.payload.totalPages,
          hasNextPage: action.payload.payload.hasNextPage
        };
      })
      .addCase(getAgentLiveTransactions.rejected, (state) => {
        state.isLoading = false;
        state.liveTransactions = [];
      });
  },
});

export default agentTransactionStatsSlice.reducer;